import { useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, type SubscriptionDiscount } from "@cliqsuite/db";
import { useAdminData } from "../state/AdminDataContext";
import { fmtDateShort } from "../lib/format";
import { PageHeaderActions } from "./PageHeaderActions";
import { DiscountModal } from "./DiscountModal";
import { ConfirmModal } from "./Modal";

function valueLabel(d: SubscriptionDiscount): string {
  return d.discount_type === "percent" ? `${d.value}% off` : `MMK ${d.value.toLocaleString("en-US")} off`;
}

/**
 * Standing discount rules — global (every business) or scoped to one
 * business, optionally narrowed to one plan. Applied automatically by
 * owner-portal's billing calculation (BillingTab.tsx), not redeemed — see
 * DiscountCodesPanel for the redeemable-code equivalent.
 */
export function DiscountsPanel() {
  const { discounts, plans, discountSave, discountDelete, discountToggle } = useAdminData();
  const { data: businesses } = useLiveQuery((q) => q.from({ b: businessesCollection }));
  const [modal, setModal] = useState<{ discount: SubscriptionDiscount | null } | null>(null);
  const [deleting, setDeleting] = useState<SubscriptionDiscount | null>(null);

  const businessName = (id: string | null) => (id ? ((businesses ?? []).find((b) => b.id === id)?.name ?? "Former business") : null);
  const planName = (id: string | null) => (id ? (plans.find((p) => p.id === id)?.name ?? "Former plan") : "All plans");

  return (
    <section className="o-card" style={{ margin: 0 }}>
      <PageHeaderActions>
        <button type="button" className="o-btn o-btn--ink" onClick={() => setModal({ discount: null })}>
          <span className="ms" aria-hidden="true">add</span>
          New discount
        </button>
      </PageHeaderActions>
      <div className="o-card__top">
        <div style={{ flex: 1 }}>
          <h2>Discounts</h2>
          <div className="o-card__sub">Applied automatically to a business or plan</div>
        </div>
      </div>
      {discounts.length === 0 && <div className="o-muted-note">No discounts yet.</div>}
      {discounts.map((d) => (
        <div key={d.id} className="o-list-row" style={{ opacity: d.is_active ? 1 : 0.6 }}>
          <span className="o-ico o-ico--soft"><span className="ms" aria-hidden="true">sell</span></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="o-row__title">{d.label || valueLabel(d)}</div>
            <div className="o-row__sub">
              {[valueLabel(d), d.scope === "global" ? "All businesses" : businessName(d.business_id), planName(d.plan_id), d.expires_at ? `until ${fmtDateShort(d.expires_at)}` : null].filter(Boolean).join(", ")}
            </div>
          </div>
          <span className="o-list-row__state">{d.is_active ? "Active" : "Off"}</span>
          <button type="button" role="switch" aria-checked={d.is_active} aria-label={d.is_active ? "Turn off" : "Turn on"} className="o-toggle" onClick={() => discountToggle(d.id, !d.is_active)}>
            <span className="o-toggle__track" aria-hidden="true" />
          </button>
          <button type="button" className="o-icon-btn" aria-label="Edit" onClick={() => setModal({ discount: d })}>
            <span className="ms" aria-hidden="true">edit</span>
          </button>
          <button type="button" className="o-icon-btn is-danger" aria-label="Delete" onClick={() => setDeleting(d)}>
            <span className="ms" aria-hidden="true">delete</span>
          </button>
        </div>
      ))}

      {deleting && (
        <ConfirmModal
          title={`Delete ${deleting.label ?? "this discount"}?`}
          body="Businesses stop getting it from their next payment."
          action="Delete"
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            discountDelete(deleting.id);
            setDeleting(null);
            setModal(null);
          }}
        />
      )}
      {modal && !deleting && (
        <DiscountModal
          discount={modal.discount}
          businesses={businesses ?? []}
          plans={plans}
          onClose={() => setModal(null)}
          onDelete={
            modal.discount
              ? () => setDeleting(modal.discount)
              : undefined
          }
          onSubmit={(form) => {
            discountSave(modal.discount?.id ?? null, form);
            setModal(null);
          }}
        />
      )}
    </section>
  );
}
